import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useDetailsNext } from '@/features/institution/hooks/useDetailsNext';
import { INSTITUTION_DETAILS_TYPE, isExemptFromAppropriatenessTest } from '@/features/institution/utils';
import { useProfileAgreement } from '@/hooks/queries/use-profile-agreement';
import { useProfileClientCategorisationTreatment } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMeta } from '@/hooks/queries/use-profile-institution-details';
import LoadingPage from '@/pages/loading';
import { AgreementKey } from '@/services/api/profile/agreement';

const InstitutionFuturesInitialRedirect = () => {
  const { isLoading: isLoadingAgreement, data: servicesAgreement } = useProfileAgreement(AgreementKey.SERVICES_AGREEMENT);
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const {
    isLoading: isLoadingInstitutionDetailsMeta,
    data: { clientCategorisation, isRegulated },
  } = useProfileInstitutionDetailsMeta();
  const { isLoading: isLoadingInstitutionDetailsNext, data: institutionDetailsNext } = useDetailsNext(INSTITUTION_DETAILS_TYPE.FUTURES);
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const { isLoading: isLoadingFuturesAppropriateness, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const { t } = useTranslation();
  const isLoading =
    isLoadingAgreement ||
    isLoadingInstitutionDetailsAnswers ||
    isLoadingInstitutionDetailsMeta ||
    isLoadingInstitutionDetailsNext ||
    isLoadingClientCategorisationTreatment ||
    isLoadingFuturesAppropriateness;

  if (isLoading)
    return (
      <LoadingPage>
        <p>{t('redirectingToNextStep')}</p>
      </LoadingPage>
    );

  if (!servicesAgreement?.date) return <Navigate to="/institution/futures/services-agreement" replace />;

  if (!institutionDetailsAnswers?.company?.leiNumber) return <Navigate to="/institution/futures/company-information" replace />;

  if (institutionDetailsNext?.route) return <Navigate to={institutionDetailsNext.route} replace />;

  if (!clientCategorisation) throw new Error('Unable to proceed with futures onboarding, no client categorisation');

  if (!clientCategorisationTreatment) return <Navigate to="/institution/futures/client-categorisation/treatment" />;

  if (isExemptFromAppropriatenessTest(isRegulated, clientCategorisationTreatment)) return <Navigate to="/institution/futures/appropriateness-test/result" replace />;

  if (!futuresAppropriateness || !('result' in futuresAppropriateness)) return <Navigate to="/institution/futures/appropriateness-test" replace />;

  return <Navigate to="/institution/futures/appropriateness-test/result" replace />;
};
InstitutionFuturesInitialRedirect.displayName = 'InstitutionFuturesInitialRedirect';

export default InstitutionFuturesInitialRedirect;
